/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { FormGroup } from 'react-bootstrap'
import Loader from '@mdsol/lego/lib/Loader'
import styled from 'styled-components'
import { useNavigate } from 'react-router'
import { useServiceContext } from '../../root'
import { useModal } from '../../contexts/ModalContext'
import WfService from '../../lib/WfService'
import { WorkflowType } from '../../constants/workflow'
import ParticipatingFsisModal from './ParticipatingFsisModal'
import TextLinkButton from '../shared/TextLinkButton'
import Modal from '../Modal'
import { checkAccessRedirectionForQC } from '../../hooks/useCheckDocAccessInQC'
import type { WorkflowCommentsResponse, WorkflowComment } from '../../services/api/workflows'
import { formatUserDateTime } from '~/utils/dateFormatting'

// Simple label component to replace ControlLabel (react-bootstrap v2 doesn't have it)
const ControlLabel: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => (
  <label className={`control-label ${className || ''}`}>{children}</label>
)

const StyledCommentItem = styled.div`
  border-bottom: 1px solid #d3d4d7;
  margin-bottom: 9px;
  padding-bottom: 11px;
  line-height: 14px;

  &:last-child {
    border-bottom: none;
  }

  .comment-header {
    margin-bottom: 8px !important;
  }
`

interface WorkflowCommentsModalProps {
  isSuwfFlow?: boolean
  workflow: any
  onClose?: () => void
  commentModalClass?: {
    commentsModalClass?: string
    commentsBackdropClass?: string
  }
}

interface WorkflowDetails {
  id: number
  workflowNo?: string
  workflowType?: string
  files?: Array<{
    sourceFsi?: { id: number; name: string }
    draftFsi?: { id: number; name: string }
    targetFsi?: { id: number; name: string }
  }>
  filename?: string
  currentAssignees?: Array<{ userId: number }>
  initiatorUserId?: number
  status?: string
}

/**
 * Workflow Comments Modal Component
 *
 * Displays all comments/reviews added during the workflow lifecycle:
 * - User name, email, and date of comment
 * - Workflow phase/step where comment was added
 * - Rejection reason (if applicable)
 * - Comment text
 *
 * Based on lego/page/workflows/comments/Modal.jsx
 */
export const WorkflowCommentsModal: React.FC<WorkflowCommentsModalProps> = ({
  workflow,
  onClose,
  commentModalClass,
  isSuwfFlow
}) => {
  const context = useServiceContext()
  const { service, api } = context
  const { translate, date } = service.filter
  const { showModal, hideAllModals } = useModal()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [isEosWf, setIsEosWf] = useState(false)
  const [workflowDetails, setWorkflowDetails] = useState<WorkflowDetails | null>(null)
  const [workingFile, setWorkingFile] = useState<any>({})
  const [comments, setComments] = useState<WorkflowComment[]>([])

  // Helper function to excerpt text (truncate with ellipsis)
  const excerpt = (text: string, maxLength: number): string => {
    if (!text) return ''
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
  }

  // Helper function to format date and time
  const formatDateTime = (timestamp: number): string => {
    return formatUserDateTime(timestamp)
  }
  useEffect(() => {
    setIsOpen(true)
  }, [])

  useEffect(() => {
    if (!workflow || !workflow.id) {
      console.error('No Workflow ID specified')
      return
    }

    setLoading(true)

    const loadAndSetComments = (wfData: WorkflowDetails) => {
      setWorkflowDetails(wfData)
      setIsEosWf(WorkflowType.EOS_DOWNLOAD_AND_ACKNOWLEDGE === wfData.workflowType)
      setWorkingFile(WfService.getWorkingFileFromWF(wfData) || {})

      api.workflows
        .getWorkflowComments(workflow.id)
        .then((data: WorkflowCommentsResponse) => {
          setComments(data.comments || [])
          setLoading(false)
        })
        .catch(() => {
          console.error('Comments could not be loaded')
          setLoading(false)
        })
    }

    // Always fetch complete workflow data to ensure we have all fields (especially files array)
    api.workflows
      .getWorkflow(workflow.id)
      .then((wfData: WorkflowDetails) => {
        loadAndSetComments(wfData)
      })
      .catch(() => {
        console.error('WF ' + workflow.id + ' not loaded')
        setLoading(false)
      })
  }, [workflow, api])

  function checkAccessQC(workflow: any, api: any, service: any, navigate: any) {
    const currentLoggedInUserId = service.auth.getUserId()
    const userRole = service.auth.getRole()
    const currentTaskAssigneeIds = workflow?.currentAssignees?.map((a: any) => a.userId) || []
    const wfId = workflow?.id
    const wfInitiatorId = workflow?.initiatorUserId
    const wfStatus = workflow?.status

    api.tasks.getTasksList({ workflowId: wfId }, true).then((taskResp: any) => {
      const tasks = taskResp?.elements || []
      const openTask = tasks?.find(
        (item: any) => item?.status === 'OPEN' && item?.assignee?.id === currentLoggedInUserId
      )
      const taskIdWhenWf = openTask?.id
      checkAccessRedirectionForQC({
        wfStatus,
        currentLoggedInUserId,
        currentTaskAssigneeIds,
        wfInitiatorId,
        wfId,
        workflowInfo: workflow,
        isWorkflow: true,
        task: taskResp,
        taskIdWhenWf,
        userRole,
        api,
        service,
        navigate
      })
    })
  }

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  return (
    <Modal
      show={isOpen}
      handleClose={doClose}
      title={translate('VIEW_COMMENTS')}
      size="md"
      className={commentModalClass?.commentsModalClass}
      id="workflow-comments-modal"
      isLevelTwoModal={isSuwfFlow}
    >
      <Loader blocking={loading}>
        <Modal.Body>
          {isEosWf && (
            <FormGroup className="wparag">
              <ControlLabel className="mb0">{translate('WORKFLOW_CHANGE_REQUEST_NUMBER')}</ControlLabel>
              <p className="whitespaceprewrap">{workflowDetails?.workflowNo}</p>
            </FormGroup>
          )}

          {!isEosWf && (
            <FormGroup className="wparag">
              <ControlLabel className="mb0">{translate('DOCUMENT')}</ControlLabel>
              <p className="whitespaceprewrap" style={{ paddingLeft: 0, marginLeft: 0 }}>
                {workflowDetails?.files && workflowDetails.files.length > 1 && (
                  <TextLinkButton
                    action={async () => {
                      if (workflow?.workflowType === WorkflowType.QC_REVIEW) {
                        // For QC workflows with multiple docs, verify access to first file
                        const firstFileId =
                          workflowDetails.files?.[0]?.sourceFsi?.id ||
                          workflowDetails.files?.[0]?.draftFsi?.id ||
                          workflowDetails.files?.[0]?.targetFsi?.id

                        if (firstFileId) {
                          try {
                            // Verify access to at least one file before proceeding
                            const fileDetails = await api.files.getDetails(Number(firstFileId))

                            // Check ACL level
                            if (fileDetails.acl === 'NONE' || fileDetails.acl === 'BROWSE') {
                              service.toast.warning(translate('GENERATING_VIEWABLE_DOC_NOT_ACCESS_MSG'))
                              return // Keep modal open, don't navigate
                            }

                            // User has access - close modal and proceed with QC access check
                            hideAllModals()
                            checkAccessQC(workflow, api, service, navigate)
                          } catch (error: any) {
                            console.error('Error fetching file details:', error)
                            if (error?.status === 403 || error?.status === 401) {
                              service.toast.warning(translate('GENERATING_VIEWABLE_DOC_NOT_ACCESS_MSG'))
                            } else {
                              service.toast.error(translate('ERROR_IN_GENERATING_PROCESS'))
                            }
                            // Keep modal open on error
                          }
                        } else {
                          // No file ID available
                          hideAllModals()
                          checkAccessQC(workflow, api, service, navigate)
                        }
                      } else {
                        // For non-QC workflows, show ParticipatingFsisModal
                        // The modal itself will handle access checks for individual files
                        showModal(<ParticipatingFsisModal wfId={workflowDetails.id} show={true} onClose={() => {}} />, {
                          keepParentModalsOpen: true
                        })
                      }
                    }}
                    style={{ padding: 0 }}
                  >
                    {translate('MULTIPLE_DOCS')}
                  </TextLinkButton>
                )}
                {workflowDetails?.files && workflowDetails.files.length === 0 && (
                  <span>{workflowDetails.filename || translate('NOT_AVAILABLE')}</span>
                )}
                {workflowDetails?.files && workflowDetails.files.length === 1 && workingFile.id && (
                  <>
                    {workflow?.workflowType === WorkflowType.QC_REVIEW ? (
                      <TextLinkButton
                        action={async () => {
                          const fileId = workingFile.id
                          if (!fileId) return

                          try {
                            // Fetch file details to verify access before navigation
                            const fileDetails = await api.files.getDetails(Number(fileId))

                            // Check ACL level
                            if (fileDetails.acl === 'NONE' || fileDetails.acl === 'BROWSE') {
                              service.toast.warning(translate('GENERATING_VIEWABLE_DOC_NOT_ACCESS_MSG'))
                              return // Keep modal open, don't navigate
                            }

                            // Check if file is valid
                            if (!fileDetails.valid) {
                              service.toast.warning(translate('GENERATING_VIEWABLE_DOC_NOT_VALID_MSG'))
                              return // Keep modal open, don't navigate
                            }

                            // Check if file is a placeholder
                            if (fileDetails.type === 'PLACEHOLDER') {
                              service.toast.warning(translate('GENERATING_VIEWABLE_DOC_PLACEHOLDER_CANNOT_OPEN'))
                              return // Keep modal open, don't navigate
                            }

                            // User has access - close modal and proceed with QC access check
                            hideAllModals()
                            checkAccessQC(workflow, api, service, navigate)
                          } catch (error: any) {
                            console.error('Error fetching file details:', error)
                            if (error?.status === 403 || error?.status === 401) {
                              service.toast.warning(translate('GENERATING_VIEWABLE_DOC_NOT_ACCESS_MSG'))
                            } else {
                              service.toast.error(translate('ERROR_IN_GENERATING_PROCESS'))
                            }
                            // Keep modal open on error
                          }
                        }}
                        className="trunk-btn-link"
                        style={{ padding: 0 }}
                      >
                        {excerpt(workingFile.name, 40)}
                      </TextLinkButton>
                    ) : (
                      <TextLinkButton
                        action={async () => {
                          const fileId = workingFile.id
                          if (!fileId) return

                          try {
                            // Fetch file details to verify access before navigation
                            const fileDetails = await api.files.getDetails(Number(fileId))

                            // Check ACL level
                            if (fileDetails.acl === 'NONE' || fileDetails.acl === 'BROWSE') {
                              service.toast.warning(translate('GENERATING_VIEWABLE_DOC_NOT_ACCESS_MSG'))
                              return // Keep modal open, don't navigate
                            }

                            // Check if file is valid
                            if (!fileDetails.valid) {
                              service.toast.warning(translate('GENERATING_VIEWABLE_DOC_NOT_VALID_MSG'))
                              return // Keep modal open, don't navigate
                            }

                            // Check if file is a placeholder
                            if (fileDetails.type === 'PLACEHOLDER') {
                              service.toast.warning(translate('GENERATING_VIEWABLE_DOC_PLACEHOLDER_CANNOT_OPEN'))
                              return // Keep modal open, don't navigate
                            }

                            // User has access - close modal and navigate
                            hideAllModals()
                            navigate(`/show-file/${fileId}?action=taskComplete`)
                          } catch (error: any) {
                            console.error('Error fetching file details:', error)
                            if (error?.status === 403 || error?.status === 401) {
                              service.toast.warning(translate('GENERATING_VIEWABLE_DOC_NOT_ACCESS_MSG'))
                            } else {
                              service.toast.error(translate('ERROR_IN_GENERATING_PROCESS'))
                            }
                            // Keep modal open on error
                          }
                        }}
                        className="trunk-btn-link"
                        style={{ padding: 0 }}
                      >
                        {excerpt(workingFile.name, 164)}
                      </TextLinkButton>
                    )}
                  </>
                )}
              </p>
            </FormGroup>
          )}

          <FormGroup className="wparag w100p mb0">
            <ControlLabel className="mb0">{translate('COMMENTS')}</ControlLabel>
            {!comments.length && <p className="fs12">{translate('NO_COMMENT_AVAILABLE')}</p>}
            {comments.map((item) => {
              const params = {
                fullName: item.userFullName,
                email: item.userEmail,
                phase: item.workflowStepTypeDescription,
                date: formatUserDateTime(item.date),
                rejectionReason: item.rejectReasonText
              }
              const commentTxtLink = translate(
                item.rejectReasonText ? 'WORKFLOW_COMMENTS_REJECTED_COMMENT' : 'WORKFLOW_COMMENTS_ADDED_COMMENT',
                params
              )

              return (
                <StyledCommentItem key={item.id} className="comment-item word-break">
                  <p className="comment-header fs12" dangerouslySetInnerHTML={{ __html: commentTxtLink }}></p>
                  {item.comment && (!item.isCustomRejectionReason || item.comment !== item.rejectReasonText) && (
                    <p className="fs12">{item.comment}</p>
                  )}
                </StyledCommentItem>
              )
            })}
          </FormGroup>
        </Modal.Body>
      </Loader>
      <Modal.Footer>
        <Modal.Footer.Button variant="primary" onClick={doClose}>
          {translate('OK')}
        </Modal.Footer.Button>
      </Modal.Footer>
    </Modal>
  )
}

export default WorkflowCommentsModal
